import { NavigateFunction } from "react-router-dom";

import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { format, isValid, max, parseISO } from "date-fns";
import { SubmissionSlotData } from "types/SubmissionSlots";

export const noop = () => void 0;

export const deepClone = <T>(toCopy: T): T => {
  return JSON.parse(JSON.stringify(toCopy)) as T;
};

export const getFirstError = (errors: {
  [key: string]: string | string[];
}): string | undefined => {
  const [error] = Object.values(errors);
  if (error) {
    return Array.isArray(error) ? error[0] : error;
  }

  return undefined;
};

/**
 * The first message in a failed request's JSON body, e.g. `nonFieldErrors`.
 * Undefined when there is no such body (network error, HTML error page).
 */
export const getApiErrorMessage = (
  error: FetchBaseQueryError | SerializedError | undefined
): string | undefined => {
  const data = error && "data" in error ? error.data : undefined;
  if (!data || typeof data !== "object") {
    return undefined;
  }

  return getFirstError(data as Record<string, string | string[]>);
};

export const clearDecimalZeros = (num: string | number): string => {
  const float = parseFloat(num.toString());

  // Return input unchanged if it's a string and:
  // - it is just numeric characters with no decimal point
  // - it doesn't consist of all numbers plus a single decimal point (which isn't at the end)
  if (typeof num === "string") {
    if (/^[-]*[0-9]+$/.test(num)) {
      return num;
    }

    if (!/^[-]*[0-9]+\.[0-9]+$/.test(num)) {
      return num;
    }
  }

  return isNaN(float) ? num.toString() : float.toString();
};

export const ObjectArrayMatcher = (
  array: { id: string; files?: any; role?: string }[],
  idToFind: string
) => {
  const matchedObject = array.find((object) => object?.id === idToFind);
  return matchedObject;
};

export const checkKeysHaveValues = (array: SubmissionSlotData) => {
  return array?.every((obj) => {
    return Object.keys(obj).every(() => {
      return (
        obj["files"] !== null &&
        obj["files"] !== undefined &&
        obj["files"].length !== 0
      );
    });
  });
};

export enum Overrides {
  NONE = "",
  ALLOW_EMPTY_LIST = "ALLOW_EMPTY_LIST",
}

export const checkActivitiesHaveValues = (
  activities: SubmissionSlotData,
  hasFileChanges: boolean,
  allowEmptyList = Overrides.NONE
) => {
  let allSurveysValid = false;

  const hasValidFiles = activities.some(
    (activity) =>
      activity.expectedFormat === "file" &&
      (activity.files?.length > 0 || allowEmptyList) &&
      hasFileChanges
  );

  const filterSurveys = activities.filter(
    (activity) => activity.expectedFormat === "survey"
  );
  if (filterSurveys.length > 0) {
    allSurveysValid = filterSurveys.every(
      (activity) =>
        activity.survey && (activity.survey.isValid || allowEmptyList)
    );
  }

  const hasSurveyAndFile =
    activities.some((activity) => activity.expectedFormat === "survey") &&
    activities.some((activity) => activity.expectedFormat === "file");

  const isSubmitEnabled = hasSurveyAndFile
    ? allSurveysValid
    : hasValidFiles || allSurveysValid;
  return isSubmitEnabled;
};

export const formattedDate = (date: Date | null) =>
  date ? format(date, "yyyy-MM-dd") : null;

export const formatNumber = (value?: number): string => {
  if (value === undefined) {
    return "";
  }
  // For values >= 1000 or <= -1000
  if (Math.abs(value) >= 1000) {
    return Math.round(value).toLocaleString();
  }

  // For values between 10 and 1000 (or -10 and -1000)
  if (Math.abs(value) >= 10) {
    return Math.round(value).toString();
  }

  // For values < 10 (and > -10)
  if (Math.abs(value) >= 1) {
    const rounded = value.toFixed(2); // Round to 2 decimal places
    // If both decimal places are zero, return as integer
    return parseFloat(rounded).toString();
  }

  // For values less than 1 (but not zero), use toPrecision to get significant figures and round trailing number
  return parseFloat(value.toPrecision(2)).toString(); // Round to 2 significant digits and remove trailing zeroes
};

export const formatStringNumber = (value?: string): string => {
  if (value === undefined) {
    return "";
  }
  const numberValue = parseFloat(value);

  // If the string is not a valid number, return the original string
  if (isNaN(numberValue)) {
    return value;
  }

  // For values >= 1000 or <= -1000
  if (Math.abs(numberValue) >= 1000) {
    return Math.round(numberValue).toLocaleString();
  }

  // For values between 10 and 1000 (or -10 and -1000)
  if (Math.abs(numberValue) >= 10) {
    return Math.round(numberValue).toString();
  }

  // For values < 10 (and > -10)
  if (Math.abs(numberValue) >= 1) {
    const rounded = numberValue.toFixed(2); // Round to 2 decimal places
    // If both decimal places are zero, return as integer
    return parseFloat(rounded).toString();
  }

  // For values less than 1 (but not zero), use toPrecision to get significant figures and round trailing number
  return parseFloat(numberValue.toPrecision(2)).toString(); // Round to 2 significant digits and remove trailing zeroes
};

export const formatString = (value: string) => {
  // Capitalize the first character and lowercase the rest
  const formattedStr =
    value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

  // Replace underscores with spaces
  return formattedStr.replace(/_/g, " ");
};

/**
 * Check if two arrays of { value: string } objects contain the same set of values,
 * regardless of ordering.
 */
export const selectOptionsHaveSameValues = (
  arr1: { value: string }[],
  arr2: { value: string }[]
): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  const set1 = new Set(arr1.map((item) => item.value));
  const set2 = new Set(arr2.map((item) => item.value));
  if (set1.size !== set2.size) {
    return false;
  }
  for (const value of set1) {
    if (!set2.has(value)) {
      return false;
    }
  }
  return true;
};

/**
 * Convert a date string to a date object with the correct timezone offset.
 *
 * Given a date string in the format "yyyy-mm-dd", this function will return a
 * Date object with the correct timezone offset. If the date string is null or
 * undefined, the function will return null.
 *
 * @param dateString - the date string to be converted
 * @returns a Date object with the correct timezone offset, or null if the
 *          input string is null or undefined
 */
export const toGMTDate = (dateString: string | null): Date | null => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

/** Parses an ISO date string, returning null when it's empty or invalid. */
const toValidDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const date = parseISO(value);
  return isValid(date) ? date : null;
};

/**
 * Returns the later of two ISO date strings, or `undefined` when neither is
 * valid.
 *
 * Used so "Last updated" reflects the true last change to a submission slot —
 * either a customer submission (`lastUpload`) or the underlying task being
 * processed in the Hub (`lastUpdate`).
 */
export const latestDate = (
  a?: string | null,
  b?: string | null
): Date | undefined => {
  const dateA = toValidDate(a);
  const dateB = toValidDate(b);

  if (dateA && dateB) {
    return max([dateA, dateB]);
  }

  return dateA ?? dateB ?? undefined;
};

export const navigateBackOrFallback = (
  navigate: NavigateFunction,
  fallback: string
) => {
  if (globalThis.history.state && globalThis.history.state.idx > 0) {
    navigate(-1);
  } else {
    navigate(fallback);
  }
};
